import {
  HiveBodySeparator,
  HiveBodyStateComment,
  HiveBodyStateRow,
  HiveBodyStateRowCol,
  HiveBodyStateRowColTitle,
  HiveBodyStateRowColValue,
  HiveBodyStateSecTitle,
  HiveBodyStateStates,
  HiveBodyStateWrapper,
} from 'features/hive/ui/HiveBody/styles';
import {ArrowToRightIcon} from 'shared/ui/icons';
import {useDiaryStore} from 'entities/diary';
import {useApiariesStore} from 'entities/apiaries';
import {useCallback, useEffect, useState} from 'react';
import {DiaryEntry} from 'shared/types';
import {utcToUserDate} from 'features/calendar/helpers/dates.ts';

type DiaryEntriesResponse = {
  items: DiaryEntry[];
  page?: {index: number; size: number; total?: number};
};

type GetDiaryEntriesFn = (params: any) => Promise<DiaryEntriesResponse>;

export const HiveBodyState = ({hiveId}: {hiveId?: string}) => {
  const apiaryId = useApiariesStore((s) => s.currentApiaryId);
  const getDiaryEntriesTyped = useDiaryStore(
    (s) => s.getDiaryEntriesFromServer
  ) as unknown as GetDiaryEntriesFn;
  const entries = useDiaryStore((s) => s.entries);

  const [{last, prev}, setPair] = useState<{last: DiaryEntry | null; prev: DiaryEntry | null}>({
    last: null,
    prev: null,
  });
  const [loading, setLoading] = useState(false);

  const fetchDiaryEntries = useCallback(async () => {
    if (!apiaryId) return;
    setLoading(true);
    const alive = true;
    try {
      const params: any = {
        page: {size: 2, index: 0},
        sortBy: 'updatedAt',
        sort: 'DESC',
        apiaryId,
        ...(hiveId ? {hiveId} : {}),
      };
      await getDiaryEntriesTyped(params);
      if (!alive) return;
      setPair({last: entries[0] ?? null, prev: entries[1] ?? null});
    } catch {
      if (alive) setPair({last: null, prev: null});
    } finally {
      if (alive) setLoading(false);
    }
  }, [apiaryId, hiveId, getDiaryEntriesTyped]);

  useEffect(() => {
    (async () => {
      await fetchDiaryEntries();
    })();
  }, [fetchDiaryEntries]);

  const pickOld = (fromLast?: number, fromPrev?: number) => fromLast ?? fromPrev ?? undefined;

  const framesNew = last?.frames;
  const strengthNew = last?.strength;
  const strengthOld = pickOld(last?.strengthPrev, prev?.strength);
  const broodNew = last?.brood;

  const framesText = framesNew != null ? `${framesNew} шт.` : '—';
  const strengthNewText = strengthNew != null ? `${strengthNew} рам.` : '—';
  const broodText = broodNew != null ? `${broodNew} рам.` : '—';

  const tags = (last?.dictionaries || []).map((d) => d.name).filter(Boolean);
  const lastUpdated = last?.updatedAt ? utcToUserDate(last.updatedAt).toLocaleString() : null;

  return (
    <HiveBodyStateWrapper>
      <h3>Состояние семьи</h3>
      <h4>
        {loading ? 'Загружаем…' : lastUpdated ? `Последний осмотр ${lastUpdated}` : 'Данных нет'}
      </h4>
      <HiveBodySeparator />
      <HiveBodyStateRow>
        <HiveBodyStateRowCol>
          <HiveBodyStateRowColTitle>Всего рамок</HiveBodyStateRowColTitle>
          <HiveBodyStateRowColValue>{framesText}</HiveBodyStateRowColValue>
        </HiveBodyStateRowCol>
        <HiveBodyStateRowCol>
          <HiveBodyStateRowColTitle>Сила семьи</HiveBodyStateRowColTitle>
          <HiveBodyStateRowColValue>
            {strengthOld != null ? (
              <>
                <span style={{opacity: 0.35, fontWeight: 400}}>{strengthOld}</span>
                <ArrowToRightIcon />
                {strengthNewText}
              </>
            ) : (
              strengthNewText
            )}
          </HiveBodyStateRowColValue>
        </HiveBodyStateRowCol>
        <HiveBodyStateRowCol>
          <HiveBodyStateRowColTitle>Расплод</HiveBodyStateRowColTitle>
          <HiveBodyStateRowColValue>{broodText}</HiveBodyStateRowColValue>
        </HiveBodyStateRowCol>
      </HiveBodyStateRow>
      <HiveBodyStateSecTitle>Состояние</HiveBodyStateSecTitle>
      <HiveBodyStateStates>
        {tags.length ? tags.map((t) => <p key={t}>{t}</p>) : <p>—</p>}
      </HiveBodyStateStates>
      <HiveBodyStateSecTitle>Комментарий</HiveBodyStateSecTitle>
      <HiveBodyStateComment>{last?.note || '—'}</HiveBodyStateComment>
    </HiveBodyStateWrapper>
  );
};
